import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import orderApi, { Order, GeneratedProduct } from "../services/orderApi";
import funnelApi, { Funnel } from "../services/funnelApi";
import config from "../config";
import { formatCurrency } from "../utils/format";
import { notification } from "../services/notification";
import AdminSidebar from "./AdminSidebar";
import AdminOverview from "./AdminOverview";
import AdminProducts from "./AdminProducts";
import AdminFunnels from "./AdminFunnels";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "paid", label: "Paid" },
  { value: "processing", label: "Processing" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const paymentStatusOptions = [
  { value: "", label: "All Payment" },
  { value: "COD", label: "Cash on Delivery" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "refunded", label: "Refunded" },
  { value: "failed", label: "Failed" },
];

const ITEMS_PER_PAGE = 20;

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState("overview");
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [allProducts, setAllProducts] = useState<GeneratedProduct[]>([]);
  const [allFunnels, setAllFunnels] = useState<Funnel[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  
  const [currentPageNum, setCurrentPageNum] = useState(1);
  
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerPageNum, setCustomerPageNum] = useState(1);
  
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTab, setMessageTab] = useState<"conversation" | "compose">("compose");
  const [emailMessages, setEmailMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const handleLogin = () => {
    if (password === config.admin_password) {
      setIsAuthenticated(true);
      setError("");
      fetchOrders();
    } else {
      setError("Incorrect password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setCurrentPage("overview");
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await orderApi.getOrders(1, 100);
      if (response.success && response.orders) {
        setAllOrders(response.orders);
        setCurrentPageNum(1);
      } else {
        console.error("Failed to load orders:", response.error);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const response = await orderApi.getProducts(1, 100);
      if (response.success && response.products) {
        setAllProducts(response.products);
      } else {
        console.error("Failed to load products:", response.error);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const fetchFunnels = async () => {
    try {
      const response = await funnelApi.getFunnels(1, 100);
      if (response.success && response.funnels) {
        setAllFunnels(response.funnels);
      } else {
        console.error("Failed to load funnels:", response.error);
      }
    } catch (err) {
      console.error("Error fetching funnels:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await orderApi.getSettings();
      if (response.success && response.settings) {
        setSettings(response.settings);
      } else {
        console.error("Failed to load settings:", response.error);
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await orderApi.getCustomers();
      if (response.success && response.customers) {
        setCustomers(response.customers);
      } else {
        console.error("Failed to load customers:", response.error);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
      fetchProducts();
      fetchFunnels();
      fetchSettings();
      fetchCustomers();
    }
  }, [isAuthenticated]);

  const filteredOrders = useMemo(() => {
    return allOrders.filter(order => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchOrderNumber = order.orderId?.toLowerCase().includes(query);
        const matchName = order.customerName?.toLowerCase().includes(query);
        const matchEmail = order.email?.toLowerCase().includes(query);
        if (!matchOrderNumber && !matchName && !matchEmail) return false;
      }
      if (statusFilter && order.orderStatus !== statusFilter) return false;
      if (paymentStatusFilter && order.paymentStatus !== paymentStatusFilter) return false;

      // Show only active orders (non-completed)
      if (order.orderStatus === "completed") return false;
      return true;
    });
  }, [allOrders, searchQuery, statusFilter, paymentStatusFilter]);

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPageNum - 1) * ITEMS_PER_PAGE,
    currentPageNum * ITEMS_PER_PAGE
  );

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      if (customerSearchQuery) {
        const query = customerSearchQuery.toLowerCase();
        const matchName = customer.name?.toLowerCase().includes(query);
        const matchEmail = customer.email?.toLowerCase().includes(query);
        if (!matchName && !matchEmail) return false;
      }
      if (customerFilter) {
        if (customerFilter === "vip" && Number(customer.totalSpent) < 5000) return false;
        if (customerFilter === "regular" && Number(customer.totalSpent) >= 5000) return false;
        if (customerFilter === "new" && Number(customer.orderCount) > 1) return false;
        if (customerFilter === "returning" && Number(customer.orderCount) <= 1) return false;
      }
      return true;
    });
  }, [customers, customerSearchQuery, customerFilter]);

  const customerTotalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
  const paginatedCustomers = filteredCustomers.slice(
    (customerPageNum - 1) * ITEMS_PER_PAGE,
    customerPageNum * ITEMS_PER_PAGE
  );

  const handleStatusChange = async () => {
    if (!selectedOrder) return;
    setSaving(true);
    try {
      await orderApi.updateOrderStatus(selectedOrder.orderId, selectedStatus);
      await orderApi.updatePaymentStatus(selectedOrder.orderId, selectedPaymentStatus);
      setShowStatusModal(false);
      setSelectedOrder(null);
      setSelectedStatus("");
      setSelectedPaymentStatus("");
      fetchOrders();
    } catch (err) {
      console.error("Error updating status:", err);
      notification.error("Error updating status");
    }
    setSaving(false);
  };

  const openMessageModal = async (order: Order) => {
    setSelectedOrder(order);
    setShowMessageModal(true);
    setMessageTab("compose");
    setEmailSubject(`Order #${order.orderId} - Update`);
    setEmailMessage("");
    
    // Try to load conversation
    setLoadingMessages(true);
    try {
      const response = await orderApi.getGmailThread(order.email, order.orderId);
      if (response.success && response.messages) {
        setEmailMessages(response.messages);
        if (response.messages.length > 0) {
          setMessageTab("conversation");
        }
      }
    } catch (err) {
      console.error("Error loading email thread:", err);
    }
    setLoadingMessages(false);
  };

const handleSendEmail = async () => {
    if (!selectedOrder || !emailSubject || !emailMessage) return;
    setSendingEmail(true);
    try {
      await orderApi.sendCustomerEmail(
         selectedOrder.email,
         selectedOrder.orderId,
         selectedOrder.customerName,
         emailSubject,
         emailMessage
       );
      notification.success("Email sent successfully!");
       setEmailMessage("");
       setMessageTab("conversation");
       // Refresh messages
       const response = await orderApi.getGmailThread(selectedOrder.email, selectedOrder.orderId);
       if (response.success && response.messages) {
         setEmailMessages(response.messages);
       }
     } catch (err) {
       console.error("Error sending email:", err);
       notification.error("Error sending email");
     }
    setSendingEmail(false);
  };

  const testEmailApi = async () => {
    const testEmail = prompt("Enter test email address:");
    if (!testEmail) return;
    try {
      const response = await fetch(`${config.appscript_url}?action=testEmailSend&email=${encodeURIComponent(testEmail)}`);
      const result = await response.text();
      alert("Result: " + result);
    } catch (err) {
      alert("Error: " + err);
    }
  };

  const openStatusModal = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.orderStatus || "");
    setSelectedPaymentStatus(order.paymentStatus || "");
    setShowStatusModal(true);
  };

  const lowStockProducts = allProducts.filter(p => p.stock !== undefined && p.stock <= 0);

  const renderNotificationsPage = () => (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Notifications</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>{lowStockProducts.length} alerts</p>
        </div>
        <button onClick={fetchProducts} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      {lowStockProducts.length === 0 ? (
        <div style={{ background: "white", borderRadius: "16px", padding: "60px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <i className="fas fa-check-circle" style={{ fontSize: "48px", color: "#10b981", marginBottom: "16px" }}></i>
          <h3 style={{ color: "#1a1a1a", marginBottom: "8px" }}>All Good!</h3>
          <p style={{ color: "#6b7280" }}>No stock alerts at the moment.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {lowStockProducts.map((product) => (
            <div key={product.productId} style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", borderLeft: "4px solid #ef4444" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "8px", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} style={{ width: "100%", height: "100%", borderRadius: "8px", objectFit: "cover" }} />
                  ) : (
                    <i className="fas fa-box" style={{ color: "#ef4444", fontSize: "20px" }}></i>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontWeight: "600", margin: "0 0 4px", fontSize: "0.95rem" }}>{product.name}</h4>
                  <p style={{ margin: 0, color: "#ef4444", fontSize: "0.85rem", fontWeight: "600" }}>
                    <i className="fas fa-exclamation-triangle"></i> Out of stock
                  </p>
                </div>
                <button 
                  onClick={() => {
                    setCurrentPage("products");
                  }}
                  style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontWeight: "600", color: "#6366f1", fontSize: "0.85rem" }}
                >
                  <i className="fas fa-edit"></i> Restock
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderSettingsPage = () => (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Settings</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>System configuration</p>
        </div>
        <button onClick={fetchSettings} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div style={{ background: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "20px", borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ fontWeight: "600", margin: "0 0 4px" }}>App Information</h3>
          <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: 0 }}>Basic application settings</p>
        </div>
        
        {settings ? (
          <div style={{ padding: "20px" }}>
            <div style={{ display: "grid", gap: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>App Name</span>
                <span style={{ fontWeight: "600" }}>{settings.appName || "-"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Version</span>
                <span style={{ fontWeight: "600" }}>{settings.version || "-"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Currency Symbol</span>
                <span style={{ fontWeight: "600" }}>{settings.currencySymbol || "₱"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Order ID Prefix</span>
                <span style={{ fontWeight: "600" }}>{settings.orderIdPrefix || "ORD"}</span>
              </div>
            </div>

            <div style={{ padding: "20px", borderBottom: "1px solid #e5e7eb", marginTop: "20px" }}>
              <h3 style={{ fontWeight: "600", margin: "0 0 4px" }}>Email Settings</h3>
              <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: "0 0 16px" }}>Order confirmation email configuration</p>
            </div>

            <div style={{ display: "grid", gap: "16px", padding: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Send Order Email</span>
                <span style={{ fontWeight: "600", color: settings.sendOrderEmail ? "#10b981" : "#ef4444" }}>
                  {settings.sendOrderEmail ? "Enabled" : "Disabled"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Sender Name</span>
                <span style={{ fontWeight: "600" }}>{settings.senderName || "-"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Order Email</span>
                <span style={{ fontWeight: "600" }}>{settings.orderEmail || "-"}</span>
              </div>
            </div>

            {settings.emailQuota && (
              <>
                <div style={{ padding: "20px", borderBottom: "1px solid #e5e7eb" }}>
                  <h3 style={{ fontWeight: "600", margin: "0 0 4px" }}>Email Quota</h3>
                  <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: "0" }}>Daily Gmail remaining quota</p>
                </div>

                <div style={{ padding: "20px" }}>
                  <div style={{ 
                    padding: "24px", 
                    background: settings.emailQuota.remaining > 50 ? "#d1fae5" : settings.emailQuota.remaining > 10 ? "#fef3c7" : "#fee2e2", 
                    borderRadius: "12px", 
                    textAlign: "center" 
                  }}>
                    <p style={{ fontSize: "0.85rem", color: settings.emailQuota.remaining > 50 ? "#065f46" : settings.emailQuota.remaining > 10 ? "#92400e" : "#991b1b", margin: "0 0 8px" }}>
                      Remaining Emails
                    </p>
                    <p style={{ fontSize: "2.5rem", fontWeight: "700", margin: 0, color: settings.emailQuota.remaining > 50 ? "#059669" : settings.emailQuota.remaining > 10 ? "#d97706" : "#dc2626" }}>
                      {settings.emailQuota.remaining}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div style={{ padding: "20px", borderBottom: "1px solid #e5e7eb" }}>
              <h3 style={{ fontWeight: "600", margin: "0 0 4px" }}>Integration</h3>
              <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: "0 0 16px" }}>Connected services</p>
            </div>

            <div style={{ padding: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
                <span style={{ color: "#6b7280" }}>Spreadsheet ID</span>
                <span style={{ fontWeight: "600", fontSize: "0.85rem", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {settings.spreadsheetId || "-"}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>Loading settings...</div>
        )}
      </div>
    </>
  );

  const renderCustomersPage = () => (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Customers</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>{filteredCustomers.length} customers</p>
        </div>
        <button onClick={fetchCustomers} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div style={{ background: "white", padding: "16px", borderRadius: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input 
            type="text" 
            placeholder="Search customers..." 
            value={customerSearchQuery} 
            onChange={(e) => { setCustomerSearchQuery(e.target.value); setCustomerPageNum(1); }}
            style={{ flex: "1", minWidth: "180px", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb" }} 
          />
          <select 
            value={customerFilter} 
            onChange={(e) => { setCustomerFilter(e.target.value); setCustomerPageNum(1); }}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white" }}
          >
            <option value="">All Customers</option>
            <option value="vip">VIP (₱5,000+)</option>
            <option value="regular">Regular (Below ₱5,000)</option>
            <option value="new">New (1 order)</option>
            <option value="returning">Returning (2+ orders)</option>
          </select>
        </div>
      </div>

      {customers.length === 0 ? (
        <div style={{ background: "white", borderRadius: "16px", padding: "60px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <i className="fas fa-users" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }}></i>
          <h3 style={{ color: "#6b7280", marginBottom: "8px" }}>No customers yet</h3>
          <p style={{ color: "#9ca3af" }}>Customers will appear here after placing orders.</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div style={{ background: "white", borderRadius: "16px", padding: "60px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <i className="fas fa-search" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }}></i>
          <h3 style={{ color: "#6b7280", marginBottom: "8px" }}>No customers found</h3>
          <p style={{ color: "#9ca3af" }}>Try adjusting your search or filter.</p>
        </div>
      ) : (
        <>
          <div style={{ background: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
            <div style={{ overflowX: "auto" }} className="admin-table-wrapper">
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }} className="admin-table">
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Customer</th>
                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Email</th>
                    <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Orders</th>
                    <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Total Spent</th>
                    <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Last Order</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCustomers.map((customer, idx) => (
                    <tr key={idx} style={{ borderTop: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px", fontWeight: "500" }}>{customer.name || "-"}</td>
                      <td style={{ padding: "12px", color: "#6366f1" }}>{customer.email}</td>
                      <td style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>{customer.orderCount}</td>
                      <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#10b981" }}>₱{Number(customer.totalSpent).toFixed(2)}</td>
                      <td style={{ padding: "12px", textAlign: "center", fontSize: "0.85rem", color: "#6b7280" }}>
                        {customer.lastOrderDate ? new Date(customer.lastOrderDate).toLocaleDateString() : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div style={{ padding: "12px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Page {customerPageNum} of {customerTotalPages}</span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button aria-label="Previous page" onClick={() => setCustomerPageNum(p => Math.max(1, p - 1))} disabled={customerPageNum <= 1} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: customerPageNum <= 1 ? "#f3f4f6" : "white", cursor: customerPageNum <= 1 ? "not-allowed" : "pointer" }}>Prev</button>
              <button aria-label="Next page" onClick={() => setCustomerPageNum(p => Math.min(customerTotalPages, p + 1))} disabled={customerPageNum >= customerTotalPages} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: customerPageNum >= customerTotalPages ? "#f3f4f6" : "white", cursor: customerPageNum >= customerTotalPages ? "not-allowed" : "pointer" }}>Next</button>
            </div>
          </div>
        </>
      )}
    </>
  );

  const renderContent = () => {
    switch (currentPage) {
      case "overview": return <AdminOverview orders={allOrders} onRefresh={fetchOrders} onCardClick={(filterType, filterValue) => {
        setCurrentPage("orders");
        if (filterType === "paymentStatus") {
          setPaymentStatusFilter(filterValue);
          setStatusFilter("");
        } else {
          setStatusFilter(filterValue);
          setPaymentStatusFilter("");
        }
        setCurrentPageNum(1);
      }} />;
      case "notifications": return renderNotificationsPage();
      case "orders": return renderOrdersPage();
      case "completed-orders": return renderCompletedOrdersPage();
      case "products": return <AdminProducts products={allProducts} onProductsUpdate={fetchProducts} />;
      case "funnels": return <AdminFunnels funnels={allFunnels} onFunnelsUpdate={fetchFunnels} />;
      case "settings": return renderSettingsPage();
      case "customers": return renderCustomersPage();
      default:
        return (
          <div style={{ background: "white", borderRadius: "16px", padding: "40px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
            <i className="fas fa-cog" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }}></i>
            <h3 style={{ color: "#6b7280" }}>{currentPage.charAt(0).toUpperCase() + currentPage.slice(1)}</h3>
            <p style={{ color: "#9ca3af" }}>Coming soon...</p>
          </div>
        );
    }
  };

  const renderOrdersPage = () => (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Active Orders</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>{filteredOrders.length} orders</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={testEmailApi} style={{ background: "#f59e0b", color: "white", padding: "10px 16px", borderRadius: "10px", border: "none", cursor: "pointer", display: "none" }}>
            <i className="fas fa-envelope"></i> Test Email
          </button>
          <button onClick={fetchOrders} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>
      
      <div style={{ background: "white", padding: "16px", borderRadius: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input 
            type="text" 
            placeholder="Search..." 
            value={searchQuery} 
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPageNum(1); }}
            style={{ flex: "1", minWidth: "180px", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb" }} 
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPageNum(1); }}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white" }}
          >
            {statusOptions.filter(opt => opt.value !== "completed").map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select 
            value={paymentStatusFilter} 
            onChange={(e) => { setPaymentStatusFilter(e.target.value); setCurrentPageNum(1); }}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white" }}
          >
            {paymentStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ background: "white", borderRadius: "12px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "60px", textAlign: "center" }}>Loading...</div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: "#6b7280" }}>No orders found</div>
        ) : (
          <div style={{ overflowX: "auto" }} className="admin-table-wrapper">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }} className="admin-table">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Products</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Order</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Customer</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Subtotal</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Shipping</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Total</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Payment</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Status</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.orderId} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "4px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                        {order.items && order.items.length > 0 ? (
                          order.items.slice(0, 3).map((item: any, idx: number) => (
                            <div key={idx} style={{ position: "relative", width: "32px", height: "32px" }}>
                              {(item as any).imageUrl ? (
                                <img src={(item as any).imageUrl} alt={item.name} style={{ width: "100%", height: "100%", borderRadius: "4px", objectFit: "cover" }} title={item.name} />
                              ) : (
                                <div style={{ width: "100%", height: "100%", borderRadius: "4px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className="fas fa-box" style={{ color: "#9ca3af", fontSize: "12px" }}></i>
                                </div>
                              )}
                            </div>
                          ))
                        ) : null}
                        {order.items && order.items.length > 3 && (
                          <div style={{ fontSize: "0.75rem", color: "#6b7280", padding: "4px 6px" }}>+{order.items.length - 3}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>{order.orderId}</td>
                    <td style={{ padding: "12px" }}>
                      <div style={{ fontWeight: "500", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={order.customerName}>{order.customerName}</div>
                      <div style={{ fontSize: "0.75rem", color: "#6b7280", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={order.email}>{order.email}</div>
                    </td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#1a1a1a" }}>{formatCurrency(Number(order.totalAmount) - Number(order.shippingFee || 0))}</td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#8b5cf6" }}>{formatCurrency(Number(order.shippingFee || 0))}</td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#f97316" }}>{formatCurrency(order.totalAmount)}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{ padding: "3px 8px", borderRadius: "12px", fontSize: "0.65rem", fontWeight: "600",
                        background: order.paymentStatus === "paid" ? "#d1fae5" : order.paymentStatus === "COD" ? "#dbeafe" : "#f3f4f6",
                        color: order.paymentStatus === "paid" ? "#059669" : order.paymentStatus === "COD" ? "#2563eb" : "#6b7280"
                      }}>{order.paymentStatus}</span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{ padding: "3px 8px", borderRadius: "12px", fontSize: "0.65rem", fontWeight: "600",
                        background: order.orderStatus === "delivered" ? "#d1fae5" : order.orderStatus === "shipped" ? "#ede9fe" : order.orderStatus === "cancelled" ? "#fee2e2" : "#fef3c7",
                        color: order.orderStatus === "delivered" ? "#059669" : order.orderStatus === "shipped" ? "#7c3aed" : order.orderStatus === "cancelled" ? "#dc2626" : "#d97706"
                      }}>{order.orderStatus}</span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button onClick={() => openMessageModal(order)} aria-label="Message Customer" title="Message Customer" style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#f59e0b" }}>
                          <i className="fas fa-envelope"></i>
                        </button>
                        <a 
                          href={`${config.host_url}/track?id=${order.orderId}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          aria-label="View Order"
                          title="View Order"
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#10b981", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                        <button onClick={() => openStatusModal(order)} aria-label="Edit Status" title="Edit Status" style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#6366f1" }}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          onClick={async () => {
                            // Check if order can be deleted
                            const protectedStatuses = ["paid", "refunded", "processing"];
                            const isProtected = protectedStatuses.includes(order.paymentStatus?.toLowerCase() || "");
                            
                            if (isProtected) {
                              await notification.alert(
                                `Cannot Delete ${order.paymentStatus} Order`,
                                `This order has payment status: <strong>${order.paymentStatus}</strong><br><br>For customer requests to cancel or refund:<br>• Update the order status instead<br>• Process refund through payment system<br>• Keep records for audit trail`,
                                'warning'
                              );
                              return;
                            }
                            
                            const confirmed = await notification.confirm(
                              "Delete Order?",
                              `Delete order <strong>${order.orderId}</strong> from ${order.customerName}?<br><br>Only pending/failed orders can be deleted.`,
                              "Yes, Delete",
                              "Cancel"
                            );
                            if (confirmed) {
                              try {
                                const result = await orderApi.deleteOrder(order.orderId);
                                if (result.success) {
                                  notification.success("Order deleted successfully!");
                                  fetchOrders();
                                } else {
                                  notification.error(result.message || result.error || "Failed to delete order");
                                }
                              } catch (err) {
                                notification.error("Error deleting order");
                                console.error(err);
                              }
                            }
                          }}
                          aria-label="Delete Order" 
                          title={["paid", "refunded", "processing"].includes(order.paymentStatus?.toLowerCase() || "") ? "Cannot delete this order (protected)" : "Delete Order"}
                          style={{ 
                            padding: "6px 8px", 
                            borderRadius: "6px", 
                            border: "1px solid #e5e7eb", 
                            background: "white", 
                            cursor: ["paid", "refunded", "processing"].includes(order.paymentStatus?.toLowerCase() || "") ? "not-allowed" : "pointer", 
                            color: ["paid", "refunded", "processing"].includes(order.paymentStatus?.toLowerCase() || "") ? "#d1d5db" : "#dc2626",
                            opacity: ["paid", "refunded", "processing"].includes(order.paymentStatus?.toLowerCase() || "") ? 0.5 : 1
                          }}
                          disabled={["paid", "refunded", "processing"].includes(order.paymentStatus?.toLowerCase() || "")}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div style={{ padding: "12px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Page {currentPageNum} of {totalPages}</span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button aria-label="Previous page" onClick={() => setCurrentPageNum(p => Math.max(1, p - 1))} disabled={currentPageNum <= 1} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum <= 1 ? "#f3f4f6" : "white", cursor: currentPageNum <= 1 ? "not-allowed" : "pointer" }}>Prev</button>
            <button aria-label="Next page" onClick={() => setCurrentPageNum(p => Math.min(totalPages, p + 1))} disabled={currentPageNum >= totalPages} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum >= totalPages ? "#f3f4f6" : "white", cursor: currentPageNum >= totalPages ? "not-allowed" : "pointer" }}>Next</button>
          </div>
        </div>
      </div>
    </>
  );

  // Filter for completed orders only
  const completedOrders = useMemo(() => {
    return allOrders.filter(order => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchOrderNumber = order.orderId?.toLowerCase().includes(query);
        const matchName = order.customerName?.toLowerCase().includes(query);
        const matchEmail = order.email?.toLowerCase().includes(query);
        if (!matchOrderNumber && !matchName && !matchEmail) return false;
      }
      if (paymentStatusFilter && order.paymentStatus !== paymentStatusFilter) return false;
      if (order.orderStatus !== "completed") return false;
      return true;
    });
  }, [allOrders, searchQuery, paymentStatusFilter]);

  const completedTotalPages = Math.ceil(completedOrders.length / ITEMS_PER_PAGE);
  const paginatedCompletedOrders = completedOrders.slice(
    (currentPageNum - 1) * ITEMS_PER_PAGE,
    currentPageNum * ITEMS_PER_PAGE
  );

  const renderCompletedOrdersPage = () => (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Completed Orders</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>{completedOrders.length} completed orders</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={fetchOrders} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>
      
      <div style={{ background: "white", padding: "16px", borderRadius: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input 
            type="text" 
            placeholder="Search..." 
            value={searchQuery} 
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPageNum(1); }}
            style={{ flex: "1", minWidth: "180px", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb" }} 
          />
          <select 
            value={paymentStatusFilter} 
            onChange={(e) => { setPaymentStatusFilter(e.target.value); setCurrentPageNum(1); }}
            style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white" }}
          >
            {paymentStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ background: "white", borderRadius: "12px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "60px", textAlign: "center" }}>Loading...</div>
        ) : completedOrders.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: "#6b7280" }}>No completed orders found</div>
        ) : (
          <div style={{ overflowX: "auto" }} className="admin-table-wrapper">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }} className="admin-table">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Products</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Order</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Customer</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Subtotal</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Shipping</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Total</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Payment</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Status</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompletedOrders.map((order) => (
                  <tr key={order.orderId} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "4px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                        {order.items && order.items.length > 0 ? (
                          order.items.slice(0, 3).map((item: any, idx: number) => (
                            <div key={idx} style={{ position: "relative", width: "32px", height: "32px" }}>
                              {(item as any).imageUrl ? (
                                <img src={(item as any).imageUrl} alt={item.name} style={{ width: "100%", height: "100%", borderRadius: "4px", objectFit: "cover" }} title={item.name} />
                              ) : (
                                <div style={{ width: "100%", height: "100%", borderRadius: "4px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className="fas fa-box" style={{ color: "#9ca3af", fontSize: "12px" }}></i>
                                </div>
                              )}
                            </div>
                          ))
                        ) : null}
                        {order.items && order.items.length > 3 && (
                          <div style={{ fontSize: "0.75rem", color: "#6b7280", padding: "4px 6px" }}>+{order.items.length - 3}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>{order.orderId}</td>
                    <td style={{ padding: "12px" }}>
                      <div style={{ fontWeight: "500", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={order.customerName}>{order.customerName}</div>
                      <div style={{ fontSize: "0.75rem", color: "#6b7280", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={order.email}>{order.email}</div>
                    </td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#1a1a1a" }}>{formatCurrency(Number(order.totalAmount) - Number(order.shippingFee || 0))}</td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#8b5cf6" }}>{formatCurrency(Number(order.shippingFee || 0))}</td>
                     <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#f97316" }}>{formatCurrency(order.totalAmount)}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{ padding: "3px 8px", borderRadius: "12px", fontSize: "0.65rem", fontWeight: "600",
                        background: order.paymentStatus === "paid" ? "#d1fae5" : order.paymentStatus === "COD" ? "#dbeafe" : "#f3f4f6",
                        color: order.paymentStatus === "paid" ? "#059669" : order.paymentStatus === "COD" ? "#2563eb" : "#6b7280"
                      }}>{order.paymentStatus}</span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{ padding: "3px 8px", borderRadius: "12px", fontSize: "0.65rem", fontWeight: "600",
                        background: order.orderStatus === "delivered" ? "#d1fae5" : order.orderStatus === "completed" ? "#ede9fe" : "#fef3c7",
                        color: order.orderStatus === "delivered" ? "#059669" : order.orderStatus === "completed" ? "#7c3aed" : "#d97706"
                      }}>{order.orderStatus}</span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button onClick={() => openMessageModal(order)} aria-label="Message Customer" title="Message Customer" style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#f59e0b" }}>
                          <i className="fas fa-envelope"></i>
                        </button>
                        <a 
                          href={`${config.host_url}/track?id=${order.orderId}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          aria-label="View Order"
                          title="View Order"
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#10b981", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                        <button onClick={() => openStatusModal(order)} aria-label="Edit Status" title="Edit Status" style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#6366f1" }}>
                          <i className="fas fa-edit"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div style={{ padding: "12px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Page {currentPageNum} of {completedTotalPages}</span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button aria-label="Previous page" onClick={() => setCurrentPageNum(p => Math.max(1, p - 1))} disabled={currentPageNum <= 1} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum <= 1 ? "#f3f4f6" : "white", cursor: currentPageNum <= 1 ? "not-allowed" : "pointer" }}>Prev</button>
            <button aria-label="Next page" onClick={() => setCurrentPageNum(p => Math.min(completedTotalPages, p + 1))} disabled={currentPageNum >= completedTotalPages} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum >= completedTotalPages ? "#f3f4f6" : "white", cursor: currentPageNum >= completedTotalPages ? "not-allowed" : "pointer" }}>Next</button>
          </div>
        </div>
      </div>
    </>
  );

  if (!isAuthenticated) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "20px", width: "100%", maxWidth: "400px" }}>
          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <div style={{ width: "60px", height: "60px", borderRadius: "16px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <i className="fas fa-lock" style={{ fontSize: "24px", color: "white" }}></i>
            </div>
            <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "8px" }}>Admin Login</h2>
            <p style={{ color: "#6b7280" }}>Enter your admin password</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <div style={{ marginBottom: "20px" }}>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password..."
                style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
              {error && <p style={{ color: "#ef4444", fontSize: "0.85rem", marginTop: "8px" }}>{error}</p>}
            </div>
            <button type="submit" style={{ width: "100%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white", padding: "14px", borderRadius: "12px", border: "none", fontWeight: "600", cursor: "pointer" }}>Login</button>
          </form>
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <Link to="/" style={{ color: "#6366f1", textDecoration: "none" }}>Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh" }}>
      <style>{`
        * {
          box-sizing: border-box;
        }
        
        /* Small phones - 320px */
        @media (max-width: 374px) {
          .admin-page-wrapper { overflow-x: hidden !important; }
          .admin-main { margin-left: 0 !important; padding: 8px !important; }
          .admin-table { min-width: 280px !important; font-size: 0.7rem !important; }
          .admin-table th, .admin-table td { padding: 6px 4px !important; }
          .admin-header h2 { font-size: 1rem !important; }
          .admin-header p { font-size: 0.75rem !important; }
          .admin-filter-row input, .admin-filter-row select { 
            font-size: 0.75rem !important; padding: 6px !important; 
          }
          .admin-modal-content { 
            padding: 8px !important; 
            margin: 4px !important;
            max-width: calc(100% - 8px) !important;
            font-size: 0.75rem !important;
          }
          .admin-btn { padding: 6px 8px !important; font-size: 0.7rem !important; }
        }
        
        /* Standard phones - 375px */
        @media (min-width: 375px) and (max-width: 413px) {
          .admin-page-wrapper { overflow-x: hidden !important; }
          .admin-main { margin-left: 0 !important; padding: 10px !important; }
          .admin-table { min-width: 320px !important; font-size: 0.75rem !important; }
          .admin-table th, .admin-table td { padding: 8px 6px !important; }
          .admin-header h2 { font-size: 1.1rem !important; }
          .admin-filter-row input, .admin-filter-row select { 
            font-size: 0.8rem !important; padding: 8px !important; 
          }
          .admin-modal-content { 
            padding: 12px !important; 
            margin: 6px !important;
            max-width: calc(100% - 12px) !important;
            font-size: 0.8rem !important;
          }
        }
        
        /* Large phones - 414px */
        @media (min-width: 414px) and (max-width: 767px) {
          .admin-page-wrapper { overflow-x: hidden !important; }
          .admin-main { margin-left: 0 !important; padding: 12px !important; }
          .admin-table { min-width: 380px !important; }
          .admin-header h2 { font-size: 1.2rem !important; }
          .admin-modal-content { 
            padding: 16px !important; 
            margin: 8px !important;
            max-width: calc(100% - 16px) !important;
          }
        }
        
        /* Tablet - 768px */
        @media (min-width: 768px) and (max-width: 1024px) {
          .admin-main { margin-left: 0 !important; padding: 16px !important; }
          .admin-table { min-width: 500px !important; }
        }
        
        /* Desktop */
        @media (min-width: 1025px) {
          .admin-main { margin-left: 260px !important; }
        }
      `}</style>
      <div style={{ background: "#f9fafb", minHeight: "100vh" }} className="admin-page-wrapper">
        {/* Full-width Header */}
        <header style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "60px",
          background: "white",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          zIndex: 1001,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          {/* Left side: Hamburger (Mobile) or Logo (Desktop) */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              aria-label="Toggle Navigation"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                display: "none",
              }}
              className="admin-hamburger-header"
            >
              <i className={`fas ${isMobileOpen ? "fa-times" : "fa-bars"}`} style={{ fontSize: "20px", color: "#1a1a1a" }}></i>
            </button>
            <div className="admin-header-logo" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <i className="fas fa-shopping-bag" style={{ color: "white", fontSize: "16px" }}></i>
              </div>
              <div>
                <h2 style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#1a1a1a",
                  margin: 0,
                }}>
                  Admin Panel
                </h2>
              </div>
            </div>
          </div>

          {/* Right side: Notification Bell */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              aria-label="Notifications"
              onClick={() => setCurrentPage("notifications")}
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                color: currentPage === "notifications" ? "#6366f1" : "#6b7280",
                transition: "color 0.2s"
              }}
            >
              <i className="fas fa-bell" style={{ fontSize: "20px" }}></i>
              {lowStockProducts.length > 0 && (
                <span style={{
                  position: "absolute",
                  top: "6px",
                  right: "6px",
                  width: "8px",
                  height: "8px",
                  background: "#ef4444",
                  borderRadius: "50%",
                  border: "2px solid white"
                }}></span>
              )}
            </button>
          </div>
        </header>

        <style>{`
          @media (max-width: 768px) {
            .admin-hamburger-header {
              display: block !important;
            }
            .admin-header-logo {
              display: none !important;
            }
          }
        `}</style>

        <AdminSidebar
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          onLogout={handleLogout}
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
        />
        <main className="admin-main" style={{ flex: 1, marginLeft: "260px", padding: "24px", minHeight: "100vh", overflowX: "hidden", marginTop: "60px" }}>
          <div style={{ overflowX: "hidden", maxWidth: "100%" }}>
            {renderContent()}
          </div>
        </main>
      </div>

      {showStatusModal && selectedOrder && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1002, padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontWeight: "700", margin: 0 }}>Order: {selectedOrder.orderId}</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={() => { setShowStatusModal(false); openMessageModal(selectedOrder); }}
                  style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #f59e0b", background: "white", color: "#f59e0b", fontWeight: "600", cursor: "pointer", textDecoration: "none", fontSize: "0.85rem" }}
                >
                  <i className="fas fa-envelope"></i> Message
                </button>
                <a 
                  href={`${config.host_url}/track?id=${selectedOrder.orderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#10b981", color: "white", fontWeight: "600", cursor: "pointer", textDecoration: "none", fontSize: "0.85rem" }}
                >
                  <i className="fas fa-external-link-alt"></i> View Tracking Page
                </a>
              </div>
            </div>
            
            {/* Two Column Layout - Customer & Shipping */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px", marginBottom: "20px" }}>
              {/* Customer Information */}
              <div>
                <h4 style={{ fontWeight: "600", marginBottom: "12px", color: "#1a1a1a", fontSize: "0.9rem" }}>Customer Information</h4>
                <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px" }}>
                  <div style={{ display: "grid", gap: "12px" }}>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0 0 4px" }}>Name</p>
                      <p style={{ fontWeight: "500", margin: 0 }}>{selectedOrder.customerName}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0 0 4px" }}>Email</p>
                      <p style={{ fontWeight: "500", margin: 0 }}>{selectedOrder.email}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0 0 4px" }}>Phone</p>
                      <p style={{ fontWeight: "500", margin: 0 }}>{selectedOrder.phone || "N/A"}</p>
                    </div>
                    <div>
                   <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0 0 4px" }}>Total Amount</p>
                   <p style={{ fontWeight: "700", margin: 0, color: "#f97316", fontSize: "1.1rem" }}>{formatCurrency(selectedOrder.totalAmount)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Shipping Address */}
              <div>
                <h4 style={{ fontWeight: "600", marginBottom: "12px", color: "#1a1a1a", fontSize: "0.9rem" }}>Shipping Address</h4>
                <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px" }}>
                  <p style={{ fontWeight: "500", margin: "0 0 4px" }}>{selectedOrder.address || "No address"}</p>
                  <p style={{ margin: 0, color: "#6b7280" }}>{selectedOrder.city}{selectedOrder.province ? `, ${selectedOrder.province}` : ""}</p>
                  {selectedOrder.mayaRRN && (
                    <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "0 0 4px" }}>Transaction ID</p>
                      <p style={{ fontWeight: "500", margin: 0, fontSize: "0.85rem" }}>{selectedOrder.mayaRRN}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Order Items - Full Width */}
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ fontWeight: "600", marginBottom: "12px", color: "#1a1a1a", fontSize: "0.9rem" }}>Order Items</h4>
              <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px" }}>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <div style={{ display: "grid", gap: "8px" }}>
                    {selectedOrder.items.map((item: any, index: number) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px", background: "white", borderRadius: "8px" }}>
                        {item.imageUrl && (
                          <img src={item.imageUrl} alt={item.name} style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover" }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: "500", margin: 0 }}>{item.name}</p>
                                 <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: "4px 0 0" }}>Qty: {item.quantity} × {formatCurrency(item.unitPrice || 0)}</p>
                        </div>
                         <p style={{ fontWeight: "600", margin: 0, color: "#f97316" }}>{formatCurrency(item.totalPrice || 0)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "#6b7280", textAlign: "center" }}>No items</p>
                )}
              </div>
            </div>
            
            {/* Order Summary Breakdown */}
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ fontWeight: "600", marginBottom: "12px", color: "#1a1a1a", fontSize: "0.9rem" }}>Order Summary</h4>
              <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px", border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #e5e7eb" }}>
                  <span style={{ color: "#6b7280" }}>Subtotal (Products)</span>
                   <span style={{ fontWeight: "600", color: "#1a1a1a" }}>{formatCurrency(Number(selectedOrder.totalAmount) - Number(selectedOrder.shippingFee || 0))}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #e5e7eb" }}>
                  <span style={{ color: "#6b7280" }}>Shipping Fee</span>
                   <span style={{ fontWeight: "600", color: "#8b5cf6" }}>{formatCurrency(selectedOrder.shippingFee || 0)}</span>
                </div>
                 <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0" }}>
                   <span style={{ fontWeight: "700", color: "#1a1a1a", fontSize: "1.1rem" }}>Total</span>
                   <span style={{ fontWeight: "700", color: "#f97316", fontSize: "1.1rem" }}>{formatCurrency(selectedOrder.totalAmount)}</span>
                 </div>
              </div>
            </div>
            
            {/* Status Updates - Two Columns */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "20px" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "8px" }}>Payment Status</label>
                <div style={{ display: "grid", gap: "8px" }}>
                  {paymentStatusOptions.slice(1).map(opt => (
                    <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px", borderRadius: "8px", border: selectedPaymentStatus === opt.value ? "2px solid #10b981" : "1px solid #e5e7eb", background: selectedPaymentStatus === opt.value ? "#d1fae5" : "white", cursor: "pointer" }}>
                      <input type="radio" name="paymentStatus" value={opt.value} checked={selectedPaymentStatus === opt.value} onChange={(e) => setSelectedPaymentStatus(e.target.value)} />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "8px" }}>Order Status</label>
                <div style={{ display: "grid", gap: "6px" }}>
                  {statusOptions.slice(1).map(opt => (
                    <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px", borderRadius: "8px", border: selectedStatus === opt.value ? "2px solid #6366f1" : "1px solid #e5e7eb", background: selectedStatus === opt.value ? "#eef2ff" : "white", cursor: "pointer" }}>
                      <input type="radio" name="status" value={opt.value} checked={selectedStatus === opt.value} onChange={(e) => setSelectedStatus(e.target.value)} />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
              <button onClick={() => { setShowStatusModal(false); setSelectedOrder(null); }} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer" }}>Cancel</button>
              <button onClick={handleStatusChange} disabled={saving} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", background: saving ? "#9ca3af" : "#6366f1", color: "white", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {showMessageModal && selectedOrder && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1002, padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "700px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontWeight: "700", margin: 0 }}>Message Customer</h3>
              <button aria-label="Close message modal" onClick={() => { setShowMessageModal(false); setSelectedOrder(null); }} style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer" }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div style={{ marginBottom: "16px", padding: "12px", background: "#f9fafb", borderRadius: "8px" }}>
              <p style={{ margin: 0, fontSize: "0.85rem" }}><strong>To:</strong> {selectedOrder.customerName} ({selectedOrder.email})</p>
              <p style={{ margin: "4px 0 0", fontSize: "0.85rem" }}><strong>Order:</strong> {selectedOrder.orderId}</p>
            </div>
            
            {/* Tabs */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "20px", borderBottom: "1px solid #e5e7eb", paddingBottom: "12px" }}>
              <button onClick={() => setMessageTab("conversation")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: messageTab === "conversation" ? "#6366f1" : "#f3f4f6", color: messageTab === "conversation" ? "white" : "#6b7280", cursor: "pointer", fontWeight: "600" }}>
                <i className="fas fa-comments"></i> Conversation
              </button>
              <button onClick={() => setMessageTab("compose")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: messageTab === "compose" ? "#6366f1" : "#f3f4f6", color: messageTab === "compose" ? "white" : "#6b7280", cursor: "pointer", fontWeight: "600" }}>
                <i className="fas fa-pen"></i> Compose
              </button>
            </div>
            
            {messageTab === "conversation" && (
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {loadingMessages ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>Loading messages...</div>
                ) : emailMessages.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>No previous messages found</div>
                ) : (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {emailMessages.map((msg, idx) => (
                      <div key={idx} style={{ padding: "16px", background: "#f9fafb", borderRadius: "12px", borderLeft: msg.fromMe ? "3px solid #6366f1" : "3px solid #10b981" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                          <span style={{ fontWeight: "600", fontSize: "0.85rem" }}>{msg.from || "Unknown"}</span>
                          <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>{msg.date || ""}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: "0.9rem" }}>{msg.subject}</p>
                        <p style={{ margin: "8px 0 0", fontSize: "0.85rem", color: "#4b5563" }}>{msg.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {messageTab === "compose" && (
              <div>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "8px" }}>Subject</label>
                  <input 
                    type="text" 
                    value={emailSubject} 
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Email subject..."
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }}
                  />
                </div>
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "8px" }}>Message</label>
                  <textarea 
                    value={emailMessage} 
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Write your message..."
                    rows={6}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>
                <button 
                  onClick={handleSendEmail} 
                  disabled={sendingEmail || !emailSubject || !emailMessage}
                  style={{ width: "100%", padding: "14px", borderRadius: "8px", border: "none", background: sendingEmail || !emailSubject || !emailMessage ? "#9ca3af" : "#f59e0b", color: "white", fontWeight: "600", cursor: sendingEmail || !emailSubject || !emailMessage ? "not-allowed" : "pointer" }}
                >
                  {sendingEmail ? <><i className="fas fa-spinner fa-spin"></i> Sending...</> : <><i className="fas fa-paper-plane"></i> Send Email</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
